<template>
  <div>
    <modal
      v-if="showModal"
      class="feedback-modal">
      <div slot="header" class="title mb-2">
        <div class="d-flex align-center justify-space-between text">
          <div class="d-flex flex-column align-start justify-start modal-name mr-10" :class="`name-${item.slug}`">
            <h3><b>{{ item.customer.name | formatName(item.store.name.length + 5) }}'s feedback</b></h3>
            <Tooltip 
              v-if="item.customer.name.length >= 25"
              :text="`${item.customer.name}'s feedback`"
              :target="`.modal-name.name-${item.slug}`"
              :show-delay="500"
              name="longNameTooltip"
              custom-class="long-name-tooltip"
            />
            <p class="text-left phone-number" v-if="!item.customer.member_id">
              <i class="fas fa-phone-square-alt"></i> {{formatPhone(item.customer.mobile)}}
            </p>
            <p class="text-left phone-number member-link" v-else @click="redirectToMemberProfile(item.customer.member_id)">
              <i class="fas fa-phone-square-alt"></i> <u class="phone-link">{{formatPhone(item.customer.mobile)}}</u>
            </p>
          </div>
          <div class="d-flex flex-column align-end justify-middle">
            <h4 class="text-right store-name-ellipsis">
              <fai :icon="['fas', 'home']" class="mr-1" />{{ item.store.name }}
            </h4>
            <h4 class="text-right">
              <fai :icon="['far', 'clock']" class="mr-1" />{{ formatDate(item.created_at) }}
            </h4>
          </div>
        </div>
        <div class="d-flex align-center justify-start">
          <hr style="margin: 10px; width: 100%;" />
        </div>
      </div>
      <div slot="body" class="body">
        <ul v-for="(response, index) in responseList" :key="index" class="info-box mb-0">
          <li class="detail-item mb-2">
            <h4 class="modal-header">
              {{response.score_text}} 
            </h4>
            <div class="info-box-number d-flex">
              <div class="d-flex justify-start align-center modal-header">
                <i class="score" 
                   :class="{'bg-green': parseInt(item[response.score]) > 4,
                            'bg-yellow': parseInt(item[response.score]) == 4,
                            'bg-red': parseInt(item[response.score]) < 4}"
                  >
                  <span class="nps-score-in-icon">{{item[response.score]}}
                  </span>
                </i>
              </div>
              <div class="d-flex align-center justify-start stars">
                <span v-for="index in 5" :key="index">
                  <fai :icon="['fas', 'check']" class="mr-1" 
                       :class="{
                         'text-lightgrey': item[response.score] < index,
                         'text-green': item[response.score] >= index 
                                       && item[response.score] > 4,
                         'text-yellow': item[response.score] >= index 
                                        && item[response.score] == 4,
                         'text-red': item[response.score] >= index 
                                     && item[response.score] < 4
                       }" />
                </span>
              </div>
            </div>
          </li>
          <li v-if="item[response.comment] != null" class="detail-item mb-2">
            <div class="info-box-comment">
              <h4 class="modal-header">
                {{ response.comment_text }}
              </h4>
              <h5 class="d-flex align-center justify-start comment">
                {{ item[response.comment].toLowerCase() }}
              </h5>
            </div>
          </li>
        </ul>
      </div>
      <div slot="action" class="modal-footer">
        <button class="action-button button" @click="itemClickHandler()">OK</button>
      </div>
    </modal>

    <div 
      class="box mb-5 feedback-item">
      <div class="box-header with-border text-left">
        <div class="d-flex justify-start align-center">
          <div class="d-flex grow header">
            <h3 class="box-title">
              <a
                class="on-hover no-underline pr-5 name-link"
                :class="`name-${item.slug}`"
                @click="itemClickHandler()"
                >
                {{ item.customer.name | formatName(item.store.name.length) }}
              </a>
              <Tooltip 
                v-if="item.customer.name.length >= 30"
                :text="item.customer.name"
                :target="`.name-link.name-${item.slug}`"
                :show-delay="500"
                name="longNameTooltip"
                custom-class="long-name-tooltip"
              />
            </h3>
          </div>

          <div class="d-flex grow header">
            <h5><fai :icon="['far', 'clock']" />
               {{ formatDate(item.created_at) }} 
            </h5>
          </div>
          <div class="d-flex grow header">
            <h5 class="store-name-ellipsis"><fai :icon="['fas', 'home']" /> {{ item.store.name }} </h5>
          </div>

          <feedbackTagButtons
            :feedbackSlug="item.slug"
            :feedbackTags="item.user_tags"/>

          <div class="ml-auto justify-end align-center  header">
          </div>
        </div>
      </div>

      <div class="box-body text-left">
        <div class="d-flex col-sm-4">
          <div class="score-value-cell mx-3" :class="`nps-${item.slug}`">
            <div class="score-circle on-hover"
                 :class="{'bg-green': parseInt(item.nps_score) > 4,
                          'bg-yellow': parseInt(item.nps_score) == 4,
                          'bg-red': parseInt(item.nps_score) < 4}"
              >
              <h2 class="mt-0">{{ item.nps_score }}</h2>
            </div>
            <div class="text-center">
              nps
            </div>
          </div>
          <Tooltip 
            :text="scorePopoverText.nps.description"
            :target="`.score-value-cell.nps-${item.slug}`"
            :show-delay="500"
            name="npsDescription"
            custom-class="score-value-cell-tooltip nps"/>

          <li class="mb-3 fake-flex-box">
            <div class="text-left item">
              <span class="text" :class="{'text-italic': !item.nps_comment}">
                {{ item.nps_comment | formatComment }}
              </span>
            </div>
          </li>
        </div>
        <div class="d-flex col-sm-4 body-item">
          <div class="score-value-cell mx-3" :class="`crs-${item.slug}`">
            <div class="score-circle on-hover"
                 :class="{'bg-green': parseInt(item.crs_score) > 4,
                          'bg-yellow': parseInt(item.crs_score) == 4,
                          'bg-red': parseInt(item.crs_score) < 4}"
              >
              <h2 class="mt-0">{{ item.crs_score }}</h2>
            </div>
            <div class="text-center">
              cs
            </div>
          </div>
          <Tooltip 
            :text="scorePopoverText.crs.description"
            :target="`.score-value-cell.crs-${item.slug}`"
            :show-delay="500"
            name="crsDescription"
            custom-class="score-value-cell-tooltip crs"/>

          <li class="mb-3 fake-flex-box">
            <div class="text-left item">
              <span class="text" :class="{'text-italic': !item.crs_comment}">
                {{ item.crs_comment | formatComment }}
              </span>
            </div>
          </li>
        </div>
        <div class="d-flex col-sm-4 body-item">
          <div class="score-value-cell mx-3" :class="`product-${item.slug}`">
            <div class="score-circle on-hover"
                 :class="{'bg-green': item.product_score > 4,
                          'bg-yellow': item.product_score == 4,
                          'bg-red': item.product_score < 4}"
              >
              <h2 class="mt-0">{{ item.product_score }}</h2>
            </div>
            <div class="text-center">
              product
            </div>
          </div>
          <Tooltip 
            :text="scorePopoverText.product.description"
            :target="`.score-value-cell.product-${item.slug}`"
            :show-delay="500"
            name="productDescription"
            custom-class="score-value-cell-tooltip product"/>

          <li class="mb-3 fake-flex-box">
            <div class="text-left item">
              <span class="text" :class="{'text-italic': !item.product_comment}">
                {{ item.product_comment | formatComment }}
              </span>
            </div>
          </li>
        </div>
        <div class="float-stop"></div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import modal from '../../shared/Modal'
import { Tooltip } from 'uiv'
import feedbackTagButtons from './FeedbackTagButtons'

export default {
  props: {
    item: [Boolean, Object],
    questionIds: [Boolean, Object]
  },
  name: "ImportantFeedbackItem",
  components: {
    modal,
    Tooltip,
    feedbackTagButtons
  },
  data() {
    return {
      anon: "customer",
      showModal: false,
      responseList: {
        crs: {
          score_text: 'on a scale of 1 to 5 please rate your experience with our staff?',
          score: "crs_score",
          comment_text: 'we\'re sorry you were unhappy with our customer service.',
          comment: "crs_comment"
        },
        product: {
          score_text: 'on a scale of 1 to 5 please the quality of the products received?',
          score: "product_score",
          comment_text: 'we\'re sorry you were unhappy with our products.',
          comment: "product_comment"
        },
        nps: {
          score_text: 'how likely are you to refer us to your friends and family?',
          score: "nps_score",
          comment_text: 'do you have any additional feedback to help us improve your experience?',
          comment: "nps_comment"
        }
      },
      scorePopoverText: {
        nps: {
          title: "nps score",
          description:
            "this score denotes the customer's willingness to recommend your service to friends or family"
        },
        crs: {
          title: "crs score",
          description:
            "this score denotes the customer service specific aspects of their experience"
        },
        product: {
          title: "product score",
          description:
            "this score denotes the product specific aspects of their experience"
        }
      }
    }
  },
  methods: {
    tooltipText: function (type) {
      let config = this.scorePopoverText[type];
      if (!config) return false;
      return {
        title: config.title,
        content: config.description,
      };
    },
    itemClickHandler() {
      this.showModal = !this.showModal
    },
    formatDate: function(date) {
      return moment(date, "YYYY-MM-DD hh:mm:ss").format("ddd, MMM Do").toLowerCase()
    },
    formatPhone(phone) {
      return phone
        .replace(/[^0-9]/g, "")
        .replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, "+$1($2)$3-$4");
    },
    redirectToMemberProfile(member_id){
      window.location.href = `/members/${member_id}`
    },
  },
  filters: {
    formatComment: (comment) => {
      if (!comment || comment.length == 0) return "no comments";
      return comment.toLowerCase();
    },
    formatName: (name, storeNameLength) => {
      const totalLength = name.length + Math.min(storeNameLength, 35)
      if (!name) return this.anon
      if (totalLength < 65) return name.toLowerCase()

      return (name.slice(0,62 - storeNameLength) + "...").toLowerCase()
    }
  }
};
</script>
